import {useMemo, useState} from 'react';
import {ChevronRight, CornerLeftUp, Folder, Loader2, Search} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {
  FTP_SUBFOLDER_ROLE_LABELS,
  FTP_SUBFOLDER_ROLES,
  type FtpSubfolderRole,
} from '@common/groupFtp/types';

export interface PickerStart {
  path?: string;
  source?: 'GROUP_FILES' | 'PICKED';
}

const NOT_USED = 'NOT_USED';

// Browse the client's FTP folder and link one as the group's. Its subfolders' roles can be
// corrected before linking.
export function FtpFolderPickerDialog({
  vbaGroupId,
  root,
  start,
  onOpenChange,
}: {
  vbaGroupId: string;
  root: string | null;
  start: PickerStart;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [path, setPath] = useState(start.path);
  const [search, setSearch] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string>>({});

  const browse = trpc.internal.groupFtp.browse.useQuery(
    {vbaGroupId, path},
    {placeholderData: (previous) => previous},
  );
  const link = trpc.internal.groupFtp.link.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.internal.groupFtp.folder.invalidate({vbaGroupId}),
        utils.internal.groupFtp.suggestions.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
      ]);
      toast({title: browse.data?.isLinked ? 'Subfolders saved' : 'Folder linked'});
      onOpenChange(false);
    },
  });

  const open = (next: string) => {
    setPath(next);
    setSearch('');
    setRoleOverrides({});
    link.reset();
  };

  // The previous folder's listing isn't shown while the next one loads.
  const data = browse.isPlaceholderData ? undefined : browse.data;
  const top = browse.data?.root ?? root;
  const at = path ?? top;
  const atRoot = !at || at === top;
  const adminCount = data?.folders.filter((f) => f.isAdmin).length ?? 0;
  const folders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.folders ?? []).filter(
      (f) => (showAdmin || !f.isAdmin) && (!term || f.name.toLowerCase().includes(term)),
    );
  }, [data, search, showAdmin]);

  // Recognized subfolders first, so they're in view without scrolling.
  const subfolders = useMemo(
    () =>
      [...(data?.subfolders ?? [])].sort(
        (a, b) => Number(Boolean(b.role)) - Number(Boolean(a.role)),
      ),
    [data],
  );

  const roleOf = (name: string, detected: FtpSubfolderRole | null) =>
    roleOverrides[name] ?? detected ?? NOT_USED;
  const assigned = (data?.subfolders ?? []).flatMap((s) => {
    const role = roleOf(s.name, s.role);
    return role === NOT_USED ? [] : [{name: s.name, role: role as FtpSubfolderRole}];
  });

  // Drawn from the folder being opened, before its listing comes back.
  const crumbs =
    top && at
      ? [
          {name: top.replace(/^\//, ''), path: top},
          ...at
            .slice(top.length)
            .split('/')
            .filter(Boolean)
            .map((name, i, parts) => ({
              name,
              path: `${top}/${parts.slice(0, i + 1).join('/')}`,
            })),
        ]
      : [];

  const current = crumbs[crumbs.length - 1];
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;

  const submit = () => {
    if (!data || atRoot) return;
    link.mutate({
      vbaGroupId,
      path: data.path,
      source: start.source === 'GROUP_FILES' && data.path === start.path ? 'GROUP_FILES' : 'PICKED',
      subfolders: assigned,
    });
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className='grid-cols-[minmax(0,1fr)] sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Link FTP folder</DialogTitle>
          <DialogDescription>
            Open the group's folder on CBS's FTP, check what each subfolder is for, then link it.
          </DialogDescription>
        </DialogHeader>

        <nav aria-label='Folder path' className='flex flex-wrap items-center gap-1 text-sm'>
          {crumbs.map((crumb, i) => (
            <span key={crumb.path} className='flex items-center gap-1'>
              {i > 0 && <ChevronRight className='h-3.5 w-3.5 text-gray-400' />}
              {i === crumbs.length - 1 ? (
                <span className='px-1 py-0.5 font-medium'>{crumb.name}</span>
              ) : (
                <button
                  type='button'
                  className='rounded px-1 py-0.5 text-blue-700 underline-offset-2 hover:bg-blue-50 hover:underline'
                  onClick={() => open(crumb.path)}
                >
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>

        <div className='flex flex-wrap items-center gap-3'>
          <div className='relative min-w-48 flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Filter folders'
              aria-label='Filter folders'
              className='pl-9'
            />
          </div>
          {adminCount > 0 && (
            <label className='flex items-center gap-2 text-sm text-gray-600'>
              <Checkbox checked={showAdmin} onCheckedChange={(v) => setShowAdmin(v === true)} />
              Show non-group folders ({adminCount})
            </label>
          )}
        </div>

        {browse.error ? (
          <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
            {browse.error.message}
          </p>
        ) : (
          <ul className='flex h-72 flex-col divide-y overflow-y-auto rounded-md border'>
            {parent && (
              <li>
                <button
                  type='button'
                  onClick={() => open(parent.path)}
                  className='flex w-full items-center gap-2 bg-gray-50 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100'
                >
                  <CornerLeftUp className='h-4 w-4 shrink-0 text-gray-500' />
                  Back to {parent.name}
                </button>
              </li>
            )}
            {!data && (
              <li className='flex flex-1 items-center justify-center gap-2 px-3 text-sm text-gray-500'>
                <Loader2 className='h-4 w-4 animate-spin' />
                {browse.data ? `Opening ${current?.name}` : 'Opening the FTP'}
              </li>
            )}
            {data && folders.length === 0 && (
              <li className='flex flex-1 items-center justify-center px-3 text-sm text-gray-500'>
                {search ? 'No folders match' : 'No folders in here'}
                {data.fileCount > 0 && ` · ${data.fileCount} files`}
              </li>
            )}
            {folders.map((f) => (
              <li key={f.path}>
                <button
                  type='button'
                  disabled={Boolean(f.linkedToGroupId)}
                  onClick={() => open(f.path)}
                  className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent'
                >
                  <Folder className='h-4 w-4 shrink-0 text-gray-400' />
                  <span className='min-w-0 flex-1 truncate'>{f.name}</span>
                  {f.linkedToGroupId ? (
                    <span className='text-xs'>Linked to {f.linkedToGroupId}</span>
                  ) : (
                    <ChevronRight className='h-4 w-4 text-gray-400' />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {!atRoot && (
          <div className='flex flex-col gap-3 rounded-md bg-gray-50 p-3'>
            <p className='text-sm'>
              <span className='font-medium'>Subfolders of {current?.name}</span>
              <span className='text-gray-500'> · what each one is for</span>
            </p>
            {!data ? (
              <div className='flex h-52 items-center justify-center rounded-md border bg-white'>
                <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
              </div>
            ) : data.subfolders.length === 0 ? (
              <p className='text-xs text-amber-800'>
                No subfolders yet. If this is the group's folder, uploads wait until CBS adds a
                ToConcierge folder.
              </p>
            ) : (
              <ul className='h-52 divide-y overflow-y-auto rounded-md border bg-white'>
                {subfolders.map((s) => (
                  <li key={s.name} className='flex items-center justify-between gap-2 px-3 py-1.5'>
                    <span className='flex min-w-0 items-center gap-2 text-sm'>
                      <Folder className='h-4 w-4 shrink-0 text-gray-400' />
                      <span className='truncate'>{s.name}</span>
                      {s.isNew && (
                        <span className='shrink-0 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700'>
                          New
                        </span>
                      )}
                    </span>
                    <Select
                      value={roleOf(s.name, s.role)}
                      onValueChange={(role) => setRoleOverrides((o) => ({...o, [s.name]: role}))}
                    >
                      <SelectTrigger
                        aria-label={`What ${s.name} is for`}
                        className={cn(
                          'h-8 w-52 bg-white text-sm',
                          roleOf(s.name, s.role) === NOT_USED && 'text-gray-500',
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FTP_SUBFOLDER_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {FTP_SUBFOLDER_ROLE_LABELS[role]}
                          </SelectItem>
                        ))}
                        <SelectItem value={NOT_USED}>Not used</SelectItem>
                      </SelectContent>
                    </Select>
                  </li>
                ))}
              </ul>
            )}
            {!data ? null : data.subfolders.length > 0 && data.subfolders.every((s) => !s.role) ? (
              <p className='text-xs text-amber-800'>
                None of these look like a group's subfolders. If this folder holds several
                locations, open the right one.
              </p>
            ) : (
              data.subfolders.length > 0 &&
              !assigned.some((s) => s.role === 'TO_CONCIERGE') && (
                <p className='text-xs text-amber-800'>No subfolder is set for onboarding docs.</p>
              )
            )}
            {data?.linkedToGroupId && (
              <p className='text-xs text-red-700'>
                Group {data.linkedToGroupId} is already linked to this folder.
              </p>
            )}
          </div>
        )}

        {link.error && (
          <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
            {link.error.message}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              !data ||
              atRoot ||
              browse.isFetching ||
              Boolean(data.linkedToGroupId) ||
              link.isPending
            }
          >
            {link.isPending && <Loader2 className='h-4 w-4 animate-spin' />}
            {data?.isLinked ? 'Save subfolders' : 'Link this folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
